/* iter-82 — Universal Export button.

Drop this next to any tabular list in the app. Pass:
  • `title`   — the export's filename + cover title
  • `rows`    — array of objects (whatever the screen renders)
  • `columns` — [{key, label}] — which keys to extract and how to label

The button opens a small menu: Excel · Sheets (CSV) · Word · PDF.
Each option calls POST /api/admin/export and streams the result to a
file download. */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, FileType, Sheet, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { id: 'xlsx', label: 'Excel (.xlsx)',        Icon: FileSpreadsheet, color: '#16A34A' },
  { id: 'csv',  label: 'Google Sheets (.csv)', Icon: Sheet,           color: '#10B981' },
  { id: 'docx', label: 'Word (.docx)',         Icon: FileText,        color: '#1F4FBE' },
  { id: 'pdf',  label: 'PDF (.pdf)',           Icon: FileType,        color: '#DC2626' },
];

export default function ExportButton({ title, rows, columns, className = '', label = 'Export', size = 'sm', disabled = false }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef(null);

  // Hide entirely for unauthenticated viewers — backend gate is editor+.
  if (!token) return null;

  const doExport = async (format) => {
    setOpen(false);
    if (!rows || rows.length === 0) {
      toast.message('Nothing to export — the current list is empty.');
      return;
    }
    setBusy(true);
    const startToast = toast.loading(`Generating ${format.toUpperCase()}…`);
    try {
      const response = await axios.post(
        `${API}/admin/export`,
        { title: title || 'Atrium Export', format, columns, rows },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 60000,
        },
      );
      // Read the filename out of Content-Disposition if present.
      const cd = response.headers['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `atrium-export.${format}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(startToast);
      toast.success(`${filename} downloaded.`);
    } catch (e) {
      toast.dismiss(startToast);
      const msg = e?.response?.data
        ? (typeof e.response.data === 'string' ? e.response.data : await e.response.data.text?.().catch(() => '') || 'Export failed.')
        : 'Export failed.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const sizeClasses = size === 'md' ? 'px-3 py-1.5 text-xs' : 'px-2.5 py-1 text-[11px]';

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy || disabled}
        data-testid="export-button"
        className={`${sizeClasses} ${className} inline-flex items-center gap-1.5 rounded-md font-bold border transition-colors disabled:opacity-50`}
        style={{ borderColor: '#D9E3F1', color: '#1F4FBE', background: 'white', fontFamily: 'Manrope' }}
        title="Export the current view to Excel, Sheets, Word, or PDF"
      >
        <Download className="w-3 h-3" />
        {label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-30 w-52 rounded-lg shadow-lg overflow-hidden border" style={{ background: 'white', borderColor: '#E5E7EB' }} data-testid="export-menu">
            {FORMATS.map(({ id, label: l, Icon, color }) => (
              <button
                key={id}
                type="button"
                onClick={() => doExport(id)}
                data-testid={`export-format-${id}`}
                className="w-full px-3 py-2 text-xs font-bold inline-flex items-center gap-2 text-left hover:bg-gray-50"
                style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color }} />
                {l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
